import React, { useState, useEffect } from 'react';
import { Download, DollarSign, CreditCard } from 'lucide-react';
import { InvoiceDTO, PaymentDTO } from '../../services/apiClient';
import IconButton from './IconButton';
import { InvoiceStatusConstants } from '../../constants/InvoiceStatusConstants';
import { usePaymentApi } from '../../hooks/usePaymentApi';
import { useNavigate } from 'react-router';
import Button from './Button';
import Drawer from './Drawer';
import Payment, { PaymentCallbacks, PaymentConfig } from '../drawer/Payment';
import { useMemberApi } from '../../hooks/useMemberApi';
import { ComponentConstants } from '../../constants/ComponentConstants';
import { useRplApi } from '../../hooks/useRplApi';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useCompetitionEntryApi } from '../../hooks/useCompetitionEntryApi';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import { usePayFastApi } from '../../hooks/usePayFastApi';
import { FileViewer } from './FileViewer';

interface InvoiceCardProps {
  invoice: InvoiceDTO;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string; } | null>(null);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);
  const [payFastFee, setPayFastFee] = useState<number | undefined>();
  const [totalWithPayFastFee, setTotalWithPayFastFee] = useState<number | undefined>();
  const { sendProofOfPaymentLink, updateProofOfPayment: updateMemberProofOfPayment } = useMemberApi();
  const { updateProofOfPayment: updateRplProofOfPayment } = useRplApi();
  const { updateProofOfPayment: updateCourseProofOfPayment } = useCourseApi();
  const { search: searchCompetitionEntries, getById: getCompetitionEntry, edit: editCompetitionEntry } = useCompetitionEntryApi();
  const { getInvoiceSasUrl } = useInvoiceApi();
  const { calculateFee } = usePayFastApi();

  const formatCurrency = (amount?: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
    }).format(amount || 0);
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString().split('T')[0];
  };

  const handleDownload = async (invoiceId: string) => {
    setIsInvoiceLoading(true);
    try {
      const response = await getInvoiceSasUrl(invoiceId);
      if (response?.data) {
        const filename = `Invoice-${invoice.invoiceNumber || invoiceId}.pdf`;
        setViewingFile({
          name: filename,
          url: response.data,
        });
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      alert(`Error downloading invoice: ${errorMessage}`);
    }
    finally {
      setIsInvoiceLoading(false);
    }
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handlePaymentComplete = () => {
    handleDrawerClose();
  };

  const handleUploadLater = async () => {
    try {
      const memberId = invoice?.member?.id;
      if (memberId) {
        await sendProofOfPaymentLink(memberId);
      }
    } catch (error) {
      console.error('Error sending proof of payment link:', error);
    } finally {
      handlePaymentCancel();
    }
  };

  const handlePaymentCancel = () => {
    setIsDrawerOpen(false);
  };

  const getComponentType = (adminComponentId: string) => {
    switch (adminComponentId) {
      case ComponentConstants.RPL:
        return 'rpl';
      case ComponentConstants.DEDICATED_COURSES:
        return 'course';
      case ComponentConstants.MEMBERS:
        return 'member';
      case ComponentConstants.COMPETITION:
        return 'competition';
      default:
        return '';
    }
  }

  const handleProofUploaded = async (fileRecordId: string) => {
    try {
      // Determine which entity to update based on invoice component type
      if (invoice.adminComponentId === ComponentConstants.RPL && invoice.rplId) {
        // RPL: Use specialized endpoint
        await updateRplProofOfPayment(invoice.rplId, fileRecordId);
      } else if (invoice.adminComponentId === ComponentConstants.DEDICATED_COURSES && invoice.dedicatedCourseId) {
        // Course: Use specialized endpoint
        await updateCourseProofOfPayment(invoice.dedicatedCourseId, fileRecordId);
      } else if (invoice.adminComponentId === ComponentConstants.MEMBERS && invoice.memberId) {
        // Member: Use specialized endpoint
        await updateMemberProofOfPayment(invoice.memberId, fileRecordId);
      } else if (invoice.adminComponentId === ComponentConstants.COMPETITION && invoice.competitionId) {
        // Competition: More complex - need to find and update CompetitionEntry
        // Invoice links to Competition, but proof of payment is on CompetitionEntry
        await updateCompetitionEntryProofOfPayment(invoice.competitionId, invoice.memberId!, fileRecordId);
      } else {
        console.error('Unable to determine entity type or missing entity ID');
        throw new Error('Unable to update proof of payment: missing entity reference');
      }

      handleDrawerClose();
    } catch (error) {
      console.error('Error updating proof of payment:', error);
      throw error;
    }
  }

  const updateCompetitionEntryProofOfPayment = async (
    competitionId: string,
    memberId: string,
    fileRecordId: string
  ) => {
    try {
      // Find the CompetitionEntry for this competition and member
      const searchResponse = await searchCompetitionEntries({
        filters: {
          competitionId,
          memberId,
          isDeleted: false,
        },
        page: 1,
        pageSize: 1,
      });

      const entries = searchResponse?.data?.results;
      if (!entries || entries.length === 0) {
        throw new Error('Competition entry not found for this member and competition');
      }

      const competitionEntry = entries[0];

      // Get the full entry data to ensure we have all fields
      const fullEntryResponse = await getCompetitionEntry(competitionEntry.id!);
      const fullEntry = fullEntryResponse?.data;

      if (!fullEntry) {
        throw new Error('Failed to retrieve competition entry details');
      }

      // Update with proof of payment
      const updatedEntry = {
        ...fullEntry,
        proofOfPaymentFileId: fileRecordId,
        isPaymentConfirmed: true, // Mark as confirmed when proof is uploaded
      };

      await editCompetitionEntry(competitionEntry.id!, updatedEntry);
    } catch (error) {
      console.error('Error updating competition entry proof of payment:', error);
      throw error;
    }
  }

  const isPaid = invoice.invoiceStatusId === InvoiceStatusConstants.INVOICED;

  // Calculate PayFast fees when drawer opens
  useEffect(() => {
    const calculatePayFastFees = async () => {
      if (!isDrawerOpen || isPaid) {
        return;
      }

      const amount = invoice.totalIncl || 0;
      if (amount > 0) {
        try {
          const feeResponse = await calculateFee(amount);
          if (feeResponse?.data) {
            setPayFastFee(feeResponse.data.payFastFee);
            setTotalWithPayFastFee(feeResponse.data.totalWithFee);
          }
        } catch (error) {
          console.error('Error calculating PayFast fees:', error);
          setPayFastFee(undefined);
          setTotalWithPayFastFee(undefined);
        }
      }
    };

    calculatePayFastFees();
  }, [isDrawerOpen, invoice.totalIncl, isPaid]);

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className='mb-3'>
          {isPaid ? (
            <span className="text-sm text-green-600 font-medium border border rounded-md p-2">
              Paid: {formatDate(invoice.paidDate)}
            </span>
          ) : (
            <span className="text-sm text-red-600 font-medium border rounded-md p-2">
              Outstanding
            </span>
          )}
        </div>
        <hr className="my-3" />
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <CreditCard className="w-10 h-10 text-green-600" />
            <div>
              {invoice.invoiceNumber ? (
                <p className="text-sm text-gray-500">
                  {invoice.invoiceNumber}
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  {invoice.salesOrderNumber}
                </p>
              )}
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-lg font-medium text-gray-700">
                  {formatCurrency(invoice.totalIncl)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <IconButton
              type='button'
              label="Download Invoice"
              color="blue"
              icon={<Download className="w-5 h-5" />}
              onClick={() => handleDownload(invoice.id || '')}
              isLoading={isInvoiceLoading}
            />

            {!isPaid && (
              <IconButton
                type='button'
                label="Pay Invoice"
                color="green"
                icon={<DollarSign className="w-5 h-5" />}
                onClick={() => setIsDrawerOpen(true)}
              />
            )}
          </div>
        </div>
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        title="Make a Payment"
        size='small'
      >
        <div className="space-y-4">
          {invoice?.member &&
            (() => {
              const paymentConfig: PaymentConfig = {
                amount: invoice.totalIncl ?? 0,
                itemName: `${invoice.adminComponent?.title}`,
                description: 'Payment for ' + invoice.adminComponent?.title,
                reference: invoice.member?.idPassportNumber ?? '',
                payFastTransactionFee: payFastFee,
                totalWithPayFastFee: totalWithPayFastFee,
              };

              const paymentCallbacks: PaymentCallbacks = {
                onPaymentSuccess: handlePaymentComplete,
                onProofUploaded: handleProofUploaded,
                onProofPending: handleUploadLater,
                onCancel: handlePaymentCancel,
              };

              return (
                <Payment
                  member={invoice.member}
                  componentId={invoice.adminComponentId!}
                  paymentConfig={paymentConfig}
                  callbacks={paymentCallbacks}
                  title={invoice.adminComponent?.title}
                  showMemberDetails={true}
                  type={getComponentType(invoice.adminComponentId!) || ''}
                  existingInvoiceId={invoice.id}
                  existingInvoice={invoice}
                />
              );
            })()}
        </div>
      </Drawer>

      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  );
};

export default InvoiceCard;